import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createCatalogSuggestion } from '../services/api';
import { CANONICAL_UNITS } from '../utils/units';
import { KINDS } from '../utils/inventoryForm';
import { inventoryErrorMessage } from '../utils/inventoryErrors';
import {
  EMPTY_SUGGESTION_FORM,
  NAME_MAX,
  CATEGORY_MAX,
  REASON_MAX,
  validateSuggestionForm,
  buildSuggestionPayload,
  responseErrorCode,
} from '../utils/catalogSuggestions';
import HelpIcon from './help/HelpIcon';
import useEscapeKey from '../hooks/useEscapeKey';
import './SuggestInventoryItemModal.css';

const FIELD_MAX = { name: NAME_MAX, category: CATEGORY_MAX, reason: REASON_MAX };
const isFormField = (field) => ['name', 'unit', 'category', 'reason'].includes(field);

/**
 * RES-46 — La sucursal pide a la organización que agregue un insumo al catálogo bloqueado.
 *
 * @param {Object} props
 * @param {string[]} [props.categories] - Categorías existentes (datalist)
 * @param {Function} props.onClose - Cierra sin enviar
 * @param {Function} props.onCreated - Recibe la sugerencia creada (`status: 'pending'`)
 */
function SuggestInventoryItemModal({ categories = [], onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_SUGGESTION_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEscapeKey(!saving, () => onClose && onClose());

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const fieldError = (field) => {
    const code = errors[field];
    if (!code) return null;
    if (code === 'TOO_LONG') {
      return t('inventory.suggestions.errors.tooLong', { max: FIELD_MAX[field] });
    }
    if (code === 'ALREADY_IN_CATALOG') return t('inventory.suggestions.errors.alreadyInCatalog');
    if (code === 'DUPLICATE_SUGGESTION') {
      return t('inventory.suggestions.errors.duplicateSuggestion');
    }
    return t(`inventory.errors.${code}`);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const found = validateSuggestionForm(form);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSaving(true);
    setSubmitError(null);
    try {
      const suggestion = await createCatalogSuggestion(buildSuggestionPayload(form));
      if (onCreated) onCreated(suggestion);
    } catch (error) {
      console.error('Error creating catalog suggestion:', error);
      const code = responseErrorCode(error);
      const field = error?.response?.data?.field;
      if (code === 'ALREADY_IN_CATALOG' || code === 'DUPLICATE_SUGGESTION') {
        // El nombre choca con un insumo o una sugerencia pendiente: se marca en el campo
        setErrors((prev) => ({ ...prev, name: code }));
      } else if (['REQUIRED', 'TOO_LONG', 'INVALID_UNIT'].includes(code) && isFormField(field)) {
        setErrors((prev) => ({ ...prev, [field]: code }));
      } else {
        setSubmitError(inventoryErrorMessage(t, error, 'inventory.suggestions.errors.createError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const reasonLength = form.reason.length;

  return (
    <div className="modal-overlay">
      <div
        className="modal-content suggest-item-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="suggest-item-title"
      >
        <div className="modal-header">
          <h2 id="suggest-item-title">
            {t('inventory.suggestions.modal.title')} <HelpIcon topicKey="inventory.suggestions" />
          </h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.close')}
            disabled={saving}
          >
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit} noValidate>
          <div className="modal-body">
            <p className="suggest-item-intro">{t('inventory.suggestions.modal.intro')}</p>
            <p className="required-legend">* {t('common.requiredFieldsLegend')}</p>

            {submitError && (
              <div className="alert alert-error suggest-item-error" role="alert">
                {submitError}
              </div>
            )}

            <div className={`form-group${errors.name ? ' has-error' : ''}`}>
              <label htmlFor="suggest-item-name">{t('inventory.suggestions.form.name')} *</label>
              <input
                id="suggest-item-name"
                type="text"
                value={form.name}
                maxLength={NAME_MAX}
                onChange={(e) => setField('name', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.name}
              />
              {errors.name && (
                <span className="field-error" role="alert">
                  {fieldError('name')}
                </span>
              )}
            </div>

            <div className="form-row">
              <div className={`form-group${errors.unit ? ' has-error' : ''}`}>
                <label htmlFor="suggest-item-unit">{t('inventory.suggestions.form.unit')} *</label>
                <select
                  id="suggest-item-unit"
                  value={form.unit}
                  onChange={(e) => setField('unit', e.target.value)}
                  aria-required="true"
                  aria-invalid={!!errors.unit}
                >
                  <option value="">{t('inventory.suggestions.form.selectUnit')}</option>
                  {CANONICAL_UNITS.map((unit) => (
                    <option key={unit} value={unit}>
                      {t(`inventory.units.${unit}`)}
                    </option>
                  ))}
                </select>
                {errors.unit && (
                  <span className="field-error" role="alert">
                    {fieldError('unit')}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="suggest-item-kind">{t('inventory.suggestions.form.kind')}</label>
                <select
                  id="suggest-item-kind"
                  value={form.kind}
                  onChange={(e) => setField('kind', e.target.value)}
                >
                  <option value="">{t('inventory.suggestions.form.noKind')}</option>
                  {KINDS.map((kind) => (
                    <option key={kind} value={kind}>
                      {t(`inventory.kinds.${kind}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`form-group${errors.category ? ' has-error' : ''}`}>
              <label htmlFor="suggest-item-category">
                {t('inventory.suggestions.form.category')}
              </label>
              <input
                id="suggest-item-category"
                type="text"
                list="suggest-item-categories"
                value={form.category}
                maxLength={CATEGORY_MAX}
                placeholder={t('inventory.suggestions.form.categoryPlaceholder')}
                onChange={(e) => setField('category', e.target.value)}
                aria-invalid={!!errors.category}
              />
              {categories.length > 0 && (
                <datalist id="suggest-item-categories">
                  {categories.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
              )}
              {errors.category && (
                <span className="field-error" role="alert">
                  {fieldError('category')}
                </span>
              )}
            </div>

            <div className={`form-group${errors.reason ? ' has-error' : ''}`}>
              <label htmlFor="suggest-item-reason">{t('inventory.suggestions.form.reason')}</label>
              <textarea
                id="suggest-item-reason"
                rows={3}
                value={form.reason}
                placeholder={t('inventory.suggestions.form.reasonPlaceholder')}
                onChange={(e) => setField('reason', e.target.value)}
                aria-invalid={!!errors.reason}
                aria-describedby="suggest-item-reason-count"
              />
              <span
                id="suggest-item-reason-count"
                className={`suggest-item-counter${reasonLength > REASON_MAX ? ' is-over' : ''}`}
              >
                {reasonLength}/{REASON_MAX}
              </span>
              {errors.reason && (
                <span className="field-error" role="alert">
                  {fieldError('reason')}
                </span>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary suggest-item-submit" disabled={saving}>
              {saving
                ? t('inventory.suggestions.modal.sending')
                : t('inventory.suggestions.modal.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SuggestInventoryItemModal;
